import React from 'react'
import { Icon } from '../../atoms/icons/components/Icon'
import { IconProps } from '../../atoms/icons/types'
import { Text } from '@sendoutcards/quantum-design-ui'
import styles from './button.module.scss'

type buttonProps = {
  title?: string
  id?: string
  outlineColor?: string
  outlineWidth?: string
  background?: string
  backgroundColor?: string
  backgroundColorHover?: string
  textColor?: string
  textWeight?: number
  textSize?: string
  margin?: string
  padding?: string
  border?: string
  borderRadius?: string
  shouldDisplayBoxShadow?: boolean
  boxShadow?: string
  width?: string
  minWidth?: string
  maxWidth?: string
  height?: string
  whiteSpace?: 'nowrap' | 'normal' | 'pre-wrap'
  icon?: IconProps
  hoverStyle?: React.CSSProperties
  onClick?: () => void
  isDisabled?: boolean
  isSubmit?: boolean
  attentionPulse?: boolean
  attentionPulseIntervalSeconds?: number
}

/**
 * We may want to use rem units when customizing the button.
 *
 * Standard rem to px is 0.25rem = 4px.
 */
export const Button: React.FC<buttonProps> = ({
  title,
  id,
  outlineColor,
  outlineWidth,
  background,
  backgroundColor = 'rgb(190, 156, 221)',
  backgroundColorHover,
  textColor = '#FFF',
  textWeight = 400,
  textSize = '14px',
  margin,
  padding = '0.75rem 1rem',
  border = 'none',
  borderRadius = '0.5rem',
  shouldDisplayBoxShadow = false,
  boxShadow = '0px 0.25rem 1.5rem rgba(0, 0, 0, 0.25)',
  width = 'max-content',
  minWidth,
  maxWidth,
  height = 'min-content',
  whiteSpace = 'normal',
  icon,
  hoverStyle,
  onClick,
  isDisabled = false,
  isSubmit,
  attentionPulse = false,
  attentionPulseIntervalSeconds = 15,
  children,
}) => {
  const backgroundColorValue = isDisabled
    ? '#CACACA'
    : background
      ? undefined
      : backgroundColor

  const style = {
    cursor: isDisabled ? 'initial' : 'pointer',
    background: background ?? undefined,
    margin,
    padding,
    border,
    borderRadius,
    width,
    minWidth,
    maxWidth,
    height,
    outlineColor,
    outlineWidth,
    outlineStyle: outlineColor ? 'solid' : undefined,
    outlineOffset: `-${outlineWidth}`,
    color: textColor,
    fontSize: textSize,
    fontWeight: textWeight,
    whiteSpace: whiteSpace,
  } as React.CSSProperties & {
    [key: `--${string}`]: string | number | undefined
  }

  style['--soc-button-bg'] = backgroundColorValue
  style['--soc-button-bg-hover'] =
    background || isDisabled ? undefined : backgroundColorHover
  style['--soc-button-hover-transform'] = hoverStyle?.transform
  style['--soc-button-hover-box-shadow'] = hoverStyle?.boxShadow
  style['--soc-button-hover-color'] = hoverStyle?.color
  style['--soc-button-box-shadow'] = shouldDisplayBoxShadow ? boxShadow : 'none'
  style['--soc-button-pulse-interval'] = attentionPulse
    ? `${attentionPulseIntervalSeconds}s`
    : undefined

  return (
    <button
      className={[styles.button, attentionPulse ? styles.attentionPulse : null]
        .filter(Boolean)
        .join(' ')}
      style={style}
      onClick={onClick}
      disabled={isDisabled}
      type={isSubmit ? 'submit' : 'button'}
      {...(id ? { id: id } : {})}
    >
      {icon && (
        <>
          <Icon {...icon} />
          <span className={styles.iconSpacer} />
        </>
      )}

      {children}
      <Text
        content={title}
        type={'caption'}
        style={{
          color: textColor,
          fontSize: textSize,
          fontWeight: textWeight,
          whiteSpace: whiteSpace,
        }}
      />
    </button>
  )
}
